import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, ScanLine, UploadCloud, Wand2, FileText, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { inr, today, ymd } from "@/lib/format";

export default function Payments() {
  const [tab, setTab] = useState("received");
  const [parties, setParties] = useState([]);
  const [payments, setPayments] = useState([]);
  const [openDocs, setOpenDocs] = useState([]);
  const [openBills, setOpenBills] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = () => ({
    party_id: "", direction: tab, amount: 0, date: today(),
    mode: "Bank Transfer", reference: "", document_id: "",
    supplier_bill_id: "", file_id: null, notes: "",
  });
  const [form, setForm] = useState(empty());
  const [uploadedSlip, setUploadedSlip] = useState(null); // { file_id, original_filename, storage_path }
  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/payments", { params: { direction: tab } });
    setPayments(data);
  };
  useEffect(() => { load(); setForm(empty()); }, [tab]);

  useEffect(() => {
    api.get("/parties", { params: { kind: tab === "received" ? "customer" : "supplier" } })
      .then(({ data }) => setParties(data));
  }, [tab]);

  useEffect(() => {
    if (!form.party_id) { setOpenDocs([]); setOpenBills([]); return; }
    const kind = tab === "received" ? "INV" : "PO";
    api.get("/documents", { params: { kind, party_id: form.party_id } })
      .then(({ data }) => setOpenDocs(data.filter((d) => d.status !== "paid" && d.status !== "cancelled")));
    if (tab === "paid") {
      api.get("/bills").then(({ data }) =>
        setOpenBills(data.filter((b) => b.supplier_id === form.party_id && b.status !== "paid")));
    } else {
      setOpenBills([]);
    }
  }, [form.party_id, tab]);

  const resetDialog = () => { setForm(empty()); setUploadedSlip(null); };

  const onPickSlip = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("File exceeds 10 MB");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/payments/upload-slip", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedSlip(data);
      setForm((x) => ({ ...x, file_id: data.file_id }));
      toast.success("Slip uploaded — running extraction");
      await runExtract(data.file_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const runExtract = async (fid) => {
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file_id", fid);
      const { data } = await api.post("/payments/extract-slip", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ex = data.extracted || {};
      setForm((f) => ({
        ...f,
        date: (ex.date || f.date || today()).slice(0, 10),
        amount: Number(ex.amount) || f.amount || 0,
        mode: ex.mode || f.mode,
        reference: ex.reference || f.reference,
        notes: ex.notes || f.notes,
      }));
      toast.success("Auto-filled from slip. Please review & save.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const save = async () => {
    if (!form.party_id) return toast.error("Select a party");
    if (!form.amount) return toast.error("Enter an amount");
    try {
      await api.post("/payments", {
        ...form,
        direction: tab,
        document_id: form.document_id || null,
        supplier_bill_id: form.supplier_bill_id || null,
        file_id: form.file_id || null,
      });
      toast.success("Payment saved");
      setOpen(false); resetDialog(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete payment?")) return;
    await api.delete(`/payments/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle="Money received from customers and paid to suppliers"
        action={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetDialog(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-payment-btn">
                <Plus className="w-4 h-4 mr-1" /> New Payment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{tab === "received" ? "Record Received" : "Record Paid"}</DialogTitle>
              </DialogHeader>

              <div className="rounded-md border border-dashed border-indigo-200 bg-indigo-50/40 p-3 flex items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="font-medium text-slate-800 flex items-center gap-1">
                    <ScanLine className="w-4 h-4 text-indigo-600" /> Scan payment slip
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    JPG, PNG or PDF · we auto-fill date, amount, mode & reference
                  </div>
                  {uploadedSlip && (
                    <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                      <Paperclip className="w-3 h-3" /> {uploadedSlip.original_filename} attached
                    </div>
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    className="hidden"
                    onChange={onPickSlip}
                    data-testid="slip-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={extracting}
                    data-testid="scan-slip-btn"
                  >
                    {extracting ? <><Wand2 className="w-4 h-4 mr-1 animate-pulse" /> Reading…</> : <><UploadCloud className="w-4 h-4 mr-1" /> Upload & scan</>}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                <F label={tab === "received" ? "Customer" : "Supplier"}>
                  <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v, document_id: "" })}>
                    <SelectTrigger data-testid="payment-party-select"><SelectValue placeholder="Select party" /></SelectTrigger>
                    <SelectContent>
                      {parties.map((p) => (<SelectItem key={p.party_id} value={p.party_id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></F>
                <F label="Amount (₹)"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: +e.target.value })} data-testid="payment-amount" /></F>
                <F label="Mode"><Input value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} /></F>
                <F label="Reference / UTR #"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></F>
                <F label={tab === "received" ? "Against Invoice (optional)" : "Against Purchase Order (optional)"}>
                  <Select value={form.document_id || "_none"} onValueChange={(v) => setForm({ ...form, document_id: v === "_none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="On account" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">On account</SelectItem>
                      {openDocs.map((d) => (
                        <SelectItem key={d.doc_id} value={d.doc_id}>{d.number} — {inr(d.total - (d.amount_paid||0))} due</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                {tab === "paid" && (
                  <F label="Against Supplier Invoice # (optional)">
                    <Select value={form.supplier_bill_id || "_none"} onValueChange={(v) => setForm({ ...form, supplier_bill_id: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="payment-bill-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Not linked</SelectItem>
                        {openBills.map((b) => (
                          <SelectItem key={b.bill_id} value={b.bill_id}>
                            {b.invoice_number} — {inr(b.total)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </F>
                )}
                <div className="md:col-span-2">
                  <F label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} data-testid="save-payment-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-8 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="received" data-testid="tab-received">Received (from Customers)</TabsTrigger>
            <TabsTrigger value="paid" data-testid="tab-paid">Paid (to Suppliers)</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-4">
            <Card className="border-slate-200 overflow-hidden">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Party</TableHead>
                    <TableHead>Mode</TableHead><TableHead>Ref</TableHead>
                    <TableHead>Against</TableHead>
                    <TableHead>Slip</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No payments yet.</TableCell></TableRow>
                  )}
                  {payments.map((p) => (
                    <TableRow key={p.payment_id}>
                      <TableCell>{ymd(p.date)}</TableCell>
                      <TableCell className="font-medium">{p.party_name}</TableCell>
                      <TableCell>{p.mode}</TableCell>
                      <TableCell className="text-slate-500">{p.reference || "—"}</TableCell>
                      <TableCell className="text-slate-500">
                        {p.document_id && p.supplier_bill_id
                          ? "PO + Bill"
                          : p.document_id
                            ? (tab === "received" ? "Invoice" : "PO")
                            : p.supplier_bill_id
                              ? `Bill ${p.supplier_bill_number || ""}`
                              : "On account"}
                      </TableCell>
                      <TableCell>
                        {p.storage_path ? <SlipLink path={p.storage_path} name={p.file_name} /> : "—"}
                      </TableCell>
                      <TableCell className="text-right number-cell font-semibold">{inr(p.amount)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => del(p.payment_id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SlipLink({ path, name }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let blobUrl = null;
    api.get(`/files/${path}`, { responseType: "blob" }).then(({ data }) => {
      if (!alive) return;
      blobUrl = URL.createObjectURL(data);
      setUrl(blobUrl);
    }).catch(() => {});
    return () => { alive = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);
  if (!url) return <span className="text-xs text-slate-400">…</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline text-xs inline-flex items-center gap-1">
      <FileText className="w-3 h-3" /> {name || "slip"}
    </a>
  );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>; }
